import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { useApiMutation } from '@/hooks/useApi';
import { login } from '@/services';
import { storage } from '@/utils';
import type { LoginRequest } from '@/types';

export const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });
  
  const mutation = useApiMutation({
    mutationFn: login,
    onSuccess: (data) => {
      storage.set('token', data.token);
      storage.set('refreshToken', data.refreshToken);
      storage.set('user', data.user);
      navigate('/dashboard');
    },
  });
  
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutation.mutate(formData);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            登录到您的账户
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            或者{' '}
            <Link to="/" className="font-medium text-primary-600 hover:text-primary-500">
              返回首页
            </Link>
          </p>
        </div>
        
        <Card>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email-address" className="sr-only">
                  邮箱地址
                </label>
                <Input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="邮箱地址"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  密码
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="密码"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            {mutation.error && (
              <div className="text-red-600 text-sm">
                {mutation.error}
              </div>
            )}
            
            <div>
              <Button
                type="submit"
                className="group relative w-full flex justify-center btn-primary"
                disabled={mutation.isLoading}
              >
                {mutation.isLoading ? '登录中...' : '登录'}
              </Button>
            </div>
            
            <div className="text-center">
              <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
                还没有账户？立即注册
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};